import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  GraduationCap, 
  Sparkles, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  RotateCcw,
  Trophy,
  Target,
  Brain,
  ArrowRight
} from 'lucide-react';
import { useProductTraining } from '@/hooks/useProductTraining';
import { useProducts } from '@/hooks/useProducts';
import { cn } from '@/lib/utils';

export function ProductTrainingModule() {
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [selectedProductId, setSelectedProductId] = useState<string>('all');
  const [showExplanation, setShowExplanation] = useState(false);
  const [lastAnswer, setLastAnswer] = useState<{ isCorrect: boolean; correctIndex: number } | null>(null);

  const { data: products } = useProducts();
  const {
    isGenerating,
    quiz,
    currentQuestion,
    currentIndex,
    isComplete,
    score,
    totalQuestions,
    scorePercentage,
    generateQuiz,
    answerQuestion,
    resetQuiz,
  } = useProductTraining();

  const handleStartQuiz = () => {
    generateQuiz({
      productId: selectedProductId !== 'all' ? selectedProductId : undefined,
      difficulty,
      questionCount: 5,
    });
  };

  const handleAnswer = (index: number) => {
    if (showExplanation || !currentQuestion) return;
    
    const isCorrect = answerQuestion(index);
    setLastAnswer({ isCorrect: isCorrect ?? false, correctIndex: currentQuestion.correctIndex });
    setShowExplanation(true);
  };

  const handleNextQuestion = () => {
    setShowExplanation(false);
    setLastAnswer(null);
  };

  // Quiz setup screen
  if (!quiz) {
    return (
      <div className="space-y-6">
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <GraduationCap className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Product Training Module</CardTitle>
                <CardDescription>
                  Test and improve your product knowledge with AI-generated quizzes
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Product</label>
                <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All products" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {products?.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Difficulty</label>
                <Select value={difficulty} onValueChange={(v) => setDifficulty(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy - Basic Facts</SelectItem>
                    <SelectItem value="medium">Medium - Use Cases & Benefits</SelectItem>
                    <SelectItem value="hard">Hard - Objections & Strategy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Card className="bg-muted/30">
                <CardContent className="pt-4 text-center">
                  <Brain className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="font-medium">Features</p>
                  <p className="text-xs text-muted-foreground">Product capabilities</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardContent className="pt-4 text-center">
                  <Target className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="font-medium">Use Cases</p>
                  <p className="text-xs text-muted-foreground">Customer scenarios</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardContent className="pt-4 text-center">
                  <Trophy className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="font-medium">Objections</p>
                  <p className="text-xs text-muted-foreground">Handling responses</p>
                </CardContent>
              </Card>
            </div>

            <Button 
              onClick={handleStartQuiz} 
              disabled={isGenerating}
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Quiz...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Start Training Quiz
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz complete screen
  if (isComplete) {
    return (
      <Card className="border-primary/20">
        <CardHeader className="text-center">
          <div className="mx-auto p-4 rounded-full bg-primary/10 w-fit mb-4">
            <Trophy className={cn(
              "h-12 w-12",
              scorePercentage >= 80 ? "text-yellow-500" : 
              scorePercentage >= 60 ? "text-primary" : "text-muted-foreground"
            )} />
          </div>
          <CardTitle className="text-2xl">Quiz Complete!</CardTitle>
          <CardDescription>
            You scored {score} out of {totalQuestions} questions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <div className="text-5xl font-bold text-primary mb-2">{scorePercentage}%</div>
            <Badge variant={scorePercentage >= 80 ? 'default' : scorePercentage >= 60 ? 'secondary' : 'outline'}>
              {scorePercentage >= 80 ? 'Excellent!' : scorePercentage >= 60 ? 'Good Job!' : 'Keep Practicing'}
            </Badge>
          </div>

          <Progress value={scorePercentage} className="h-3" />

          <div className="flex gap-3">
            <Button onClick={resetQuiz} variant="outline" className="flex-1">
              <RotateCcw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            <Button onClick={handleStartQuiz} className="flex-1">
              <Sparkles className="mr-2 h-4 w-4" />
              New Quiz
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Active quiz screen
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              Question {currentIndex + 1} of {totalQuestions}
            </Badge>
            <Badge variant="secondary" className="capitalize">{quiz.difficulty}</Badge>
          </div>
          <div className="text-sm text-muted-foreground">
            Score: {score}/{currentIndex + (showExplanation ? 1 : 0)}
          </div>
        </div>
        <Progress value={((currentIndex + (showExplanation ? 1 : 0)) / totalQuestions) * 100} className="h-2 mt-2" />
      </CardHeader>
      <CardContent className="space-y-6">
        {currentQuestion && (
          <>
            <div className="space-y-2">
              {currentQuestion.productName && (
                <Badge variant="outline" className="mb-2">{currentQuestion.productName}</Badge>
              )}
              <h3 className="text-lg font-medium">{currentQuestion.question}</h3>
            </div>

            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const isSelected = lastAnswer && showExplanation;
                const isCorrectOption = index === currentQuestion.correctIndex;
                const wasSelected = lastAnswer && index === (currentQuestion.options.findIndex((_, i) => 
                  i === currentQuestion.correctIndex ? lastAnswer.isCorrect : !lastAnswer.isCorrect && i === lastAnswer.correctIndex
                ));

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswer(index)}
                    disabled={showExplanation}
                    className={cn(
                      "w-full p-4 text-left rounded-lg border transition-all",
                      !showExplanation && "hover:border-primary hover:bg-primary/5 cursor-pointer",
                      showExplanation && isCorrectOption && "border-green-500 bg-green-500/10",
                      showExplanation && !isCorrectOption && lastAnswer?.correctIndex !== currentQuestion.correctIndex && 
                        index === currentQuestion.options.indexOf(currentQuestion.options[currentQuestion.correctIndex]) && 
                        "border-red-500 bg-red-500/10"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option}</span>
                      {showExplanation && isCorrectOption && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                      {showExplanation && !lastAnswer?.isCorrect && index === lastAnswer?.correctIndex && (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {showExplanation && (
              <div className={cn(
                "p-4 rounded-lg",
                lastAnswer?.isCorrect ? "bg-green-500/10 border border-green-500/20" : "bg-amber-500/10 border border-amber-500/20"
              )}>
                <div className="flex items-center gap-2 mb-2">
                  {lastAnswer?.isCorrect ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-amber-500" />
                  )}
                  <span className="font-medium">
                    {lastAnswer?.isCorrect ? 'Correct!' : 'Not quite right'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{currentQuestion.explanation}</p>
              </div>
            )}

            {showExplanation && (
              <Button onClick={handleNextQuestion} className="w-full">
                {currentIndex + 1 < totalQuestions ? (
                  <>
                    Next Question
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  'See Results'
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
